import React, { useState, useMemo } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { createOpId } from '../../../services/helper/opId'
import { raiseRequest } from '../../../services/opsRequestsService'
import { submitRecord } from '../../../services/opsRegistersService'
import { queueOpsDueRecord, queueOpsRaiseRequest } from '../../../services/helper/queueService'
import {
  getSharedDeviceWorkRouterContext,
  routeRaiseRequest,
} from '../../../services/sharedDeviceWorkRouter'
import {
  PLANT_DOWNTIME_REASONS,
  PLANT_NEAR_MISS_CATEGORIES,
  type PlantDowntimeReason,
  type PlantNearMissCategory,
  type OpsAsset,
  type OpsPlace,
  type RaiseRequestInput,
  type SubmitRecordInput,
} from '../../../types/ops'
import './plant.css'

export interface PlantHelperPhoneCheck {
  id: string
  templateId: string
  title: string
  dueTime?: string
  status: 'DUE' | 'DONE' | 'MISSED'
  placeId?: string
  placeName?: string
  shiftId?: string
}

export interface PlantHelperPhoneSectionProps {
  workspaceId: string
  places: OpsPlace[]
  assets: OpsAsset[]
  currentUid: string
  isSharedDevice?: boolean
  checks?: PlantHelperPhoneCheck[]
  initialTab?: 'line' | 'scan' | 'breakdown' | 'check' | 'nearmiss'
}

export const PlantHelperPhoneSection: React.FC<PlantHelperPhoneSectionProps> = ({
  workspaceId,
  places,
  assets,
  currentUid,
  isSharedDevice,
  checks: initialChecks,
  initialTab = 'line',
}) => {
  const { t } = useNls()

  const [activeTab, setActiveTab] = useState<'line' | 'scan' | 'breakdown' | 'check' | 'nearmiss'>(
    initialTab,
  )
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [showBreakdownForm, setShowBreakdownForm] = useState(false)
  const [selectedReason, setSelectedReason] = useState<PlantDowntimeReason>('MECHANICAL')
  const [whatText, setWhatText] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Checks state
  const [checks, setChecks] = useState<PlantHelperPhoneCheck[]>(() => {
    if (initialChecks && initialChecks.length > 0) return initialChecks
    return [
      {
        id: 'chk-guard-1',
        templateId: 'tpl-guard-1',
        title: 'Loom guarding check · L1-07',
        dueTime: '15:00 · missed',
        status: 'MISSED',
        placeId: places[0]?.id || '',
      },
      {
        id: 'chk-ppe-1',
        templateId: 'tpl-ppe-1',
        title: 'PPE check',
        dueTime: 'done 14:20',
        status: 'DONE',
        placeId: places[0]?.id || '',
      },
      {
        id: 'chk-clean-1',
        templateId: 'tpl-clean-1',
        title: 'End-of-shift cleaning',
        dueTime: '21:30',
        status: 'DUE',
        placeId: places[0]?.id || '',
      },
    ]
  })
  const [selectedCheck, setSelectedCheck] = useState<PlantHelperPhoneCheck | null>(null)

  // Near miss state
  const [selectedNearMissPlaceId, setSelectedNearMissPlaceId] = useState<string>(
    () => places[0]?.id || '',
  )
  const [selectedNearMissCategory, setSelectedNearMissCategory] =
    useState<PlantNearMissCategory>('SLIP_TRIP')
  const [nearMissWhat, setNearMissWhat] = useState('')
  const [nearMissAction, setNearMissAction] = useState('')

  // Find line place
  const linePlace = useMemo(() => {
    return places.find((p) => p.level === 'LINE') || places[0] || null
  }, [places])

  const selectedAsset = useMemo(() => {
    if (!selectedAssetId) return assets[0] || null
    return assets.find((a) => a.id === selectedAssetId) || null
  }, [assets, selectedAssetId])

  const handleScanClick = () => {
    if (!selectedAsset && assets.length > 0) {
      const first = assets[0]
      if (first) {
        setSelectedAssetId(first.id)
      }
    } else if (assets.length > 0) {
      const first = assets[0]
      if (first) {
        setSelectedAssetId(first.id)
      }
    }
  }

  const handleOpenCheck = (check: PlantHelperPhoneCheck) => {
    setSelectedCheck(check)
    setActiveTab('check')
  }

  const handleSubmitCheck = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    const isShared = Boolean(isSharedDevice || routerCtx)

    const payload: SubmitRecordInput = {
      workspaceId,
      templateId: selectedCheck?.templateId || 'tpl-guard-1',
      placeId: selectedCheck?.placeId || linePlace?.id || '',
      values: { guards_fitted: true, emergency_stop: true },
      occurredAt: Date.now(),
      capturedAt: Date.now(),
      opId,
    }

    try {
      if (isShared && routerCtx) {
        await queueOpsDueRecord({
          ...payload,
          clientMutationId: opId,
        })
      } else if (isOffline) {
        await queueOpsDueRecord({
          ...payload,
          clientMutationId: opId,
        })
      } else {
        await submitRecord(payload)
      }

      if (selectedCheck) {
        setChecks((prev) =>
          prev.map((c) => (c.id === selectedCheck.id ? { ...c, status: 'DONE' } : c)),
        )
      }
      setSuccessMsg('Check submitted (locks on submit)')
      setActiveTab('line')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleRaiseNearMiss = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nearMissWhat.trim()) return

    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    const isShared = Boolean(isSharedDevice || routerCtx)

    const placeId = selectedNearMissPlaceId || linePlace?.id || places[0]?.id || ''
    const payload: RaiseRequestInput = {
      workspaceId,
      sector: 'plant',
      kind: 'NEAR_MISS',
      placeId,
      category: selectedNearMissCategory,
      whatNearlyHappened: nearMissWhat.trim(),
      immediateAction: nearMissAction.trim() || undefined,
      title: `Near miss: ${nearMissWhat.trim().slice(0, 40)}`,
      opId,
    }

    try {
      if (isShared && routerCtx) {
        await routeRaiseRequest(
          {
            ...payload,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (isOffline) {
        await queueOpsRaiseRequest({
          ...payload,
          clientMutationId: opId,
        })
      } else {
        await raiseRequest(payload)
      }

      setSuccessMsg(t('NLSOpsPlantHelperNearMissReported'))
      setNearMissWhat('')
      setNearMissAction('')
      setActiveTab('line')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleRaiseBreakdown = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedAsset) return

    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    const opId = createOpId()
    const routerCtx = getSharedDeviceWorkRouterContext()
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

    const title = whatText.trim()
      ? `${selectedAsset.name}: ${whatText.trim()}`
      : `Breakdown: ${selectedAsset.name}`

    const payload: RaiseRequestInput = {
      workspaceId,
      sector: 'plant',
      kind: 'BREAKDOWN',
      placeId: selectedAsset.placeId || '',
      assetId: selectedAsset.id,
      downtimeReason: selectedReason,
      downtimeNote: selectedReason === 'OTHER' ? whatText.trim() : undefined,
      title,
      opId,
    }

    const isShared = Boolean(isSharedDevice || routerCtx)

    try {
      if (isShared && routerCtx) {
        await routeRaiseRequest(
          {
            ...payload,
            clientMutationId: opId,
          },
          routerCtx,
        )
      } else if (isOffline) {
        await queueOpsRaiseRequest({
          ...payload,
          clientMutationId: opId,
        })
      } else {
        await raiseRequest(payload)
      }

      setSuccessMsg(t('NLSOpsPlantPhoneMarkDownBtn'))
      setWhatText('')
      setShowBreakdownForm(false)
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const getMachineStateLabel = (asset: OpsAsset) => {
    if (asset.status === 'ISOLATED') return t('NLSOpsPlantStateDown')
    if (asset.status === 'MAINTENANCE') return t('NLSOpsPlantAwaitingRelease')
    return t('NLSOpsPlantStateRunning')
  }

  return (
    <div className="plantPhone" data-testid="plant-helper-phone-section" data-user-id={currentUid}>
      {errorMsg && (
        <div className="plantPhone__error" role="alert">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="plantPhone__success" role="status">
          {successMsg}
        </div>
      )}

      {/* Surface sub-navigation */}
      <div className="plantPhone__row">
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'line' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('line')}
        >
          {t('NLSOpsPlantPhoneMyLine')}
        </button>
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'nearmiss' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('nearmiss')}
        >
          {t('NLSOpsPlantNearMissesHeader')}
        </button>
      </div>

      {activeTab === 'nearmiss' && (
        /* 4. Raise Near Miss Form (h-plt-nearmiss) */
        <form
          className="plantPhone__card"
          data-testid="plant-nearmiss-form"
          onSubmit={handleRaiseNearMiss}
        >
          <div className="plantPhone__field">
            <label className="plantPhone__label" htmlFor="plant-nearmiss-where">
              {t('NLSOpsPlantHelperWhereLabel')}
            </label>
            <select
              id="plant-nearmiss-where"
              className="plantPhone__select"
              value={selectedNearMissPlaceId}
              onChange={(e) => setSelectedNearMissPlaceId(e.target.value)}
            >
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="plantPhone__field">
            <label className="plantPhone__label">{t('NLSOpsPlantHelperCategoryLabel')}</label>
            <div className="plantPhone__chips">
              {PLANT_NEAR_MISS_CATEGORIES.map((cat) => {
                const isActive = selectedNearMissCategory === cat
                const label =
                  cat === 'SLIP_TRIP'
                    ? 'Trip / slip'
                    : cat === 'MACHINE_GUARD'
                      ? 'Guarding'
                      : cat.charAt(0) + cat.slice(1).toLowerCase().replace(/_/g, ' ')
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`plantPhone__chip ${isActive ? 'plantPhone__chip--active' : ''}`}
                    onClick={() => setSelectedNearMissCategory(cat)}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="plantPhone__field">
            <label className="plantPhone__label" htmlFor="plant-nearmiss-what">
              {t('NLSOpsPlantHelperWhatNearlyHappenedLabel')}
            </label>
            <textarea
              id="plant-nearmiss-what"
              className="plantPhone__textarea"
              data-testid="plant-nearmiss-what-input"
              value={nearMissWhat}
              onChange={(e) => setNearMissWhat(e.target.value)}
              placeholder={t('NLSOpsPlantHelperWhatNearlyHappenedPlaceholder')}
              rows={3}
            />
          </div>

          <div className="plantPhone__field">
            <label className="plantPhone__label" htmlFor="plant-nearmiss-action">
              {t('NLSOpsPlantHelperImmediateActionLabel')}
            </label>
            <input
              id="plant-nearmiss-action"
              type="text"
              className="plantPhone__input"
              data-testid="plant-nearmiss-action-input"
              value={nearMissAction}
              onChange={(e) => setNearMissAction(e.target.value)}
              placeholder={t('NLSOpsPlantHelperImmediateActionPlaceholder')}
            />
          </div>

          <div className="plantPhone__note">
            {t('NLSOpsPlantHelperNearMissNote')}
          </div>

          <button
            type="submit"
            className="plantPhone__bigBtn"
            data-testid="plant-raise-nearmiss-submit"
            disabled={busy || nearMissWhat.trim().length < 3}
          >
            {t('NLSOpsPlantHelperReportBtn')}
          </button>
        </form>
      )}

      {activeTab === 'check' && (
        /* 5. Check Record Form (h-plt-check) */
        <form
          className="plantPhone__card"
          data-testid="plant-check-form"
          onSubmit={handleSubmitCheck}
        >
          <div className="plantPhone__row">
            <h4 className="plantPhone__title">
              {selectedCheck?.title || 'Check record'}
            </h4>
            <span className="plantPhone__pill plantPhone__pill--warn">
              {selectedCheck?.dueTime || 'Due'}
            </span>
          </div>

          <div className="plantPhone__meta">
            {linePlace ? linePlace.name : 'Line 1'} · Shift
          </div>

          <div className="plantPhone__field" style={{ marginTop: '12px' }}>
            <div className="plantPhone__row">
              <span>All guards fitted</span>
              <span className="plantPhone__pill plantPhone__pill--good">Yes</span>
            </div>
            <div className="plantPhone__row" style={{ marginTop: '8px' }}>
              <span>Emergency stop works</span>
              <span className="plantPhone__pill plantPhone__pill--good">Yes</span>
            </div>
            <div className="plantPhone__row" style={{ marginTop: '8px' }}>
              <span>Area clear of loose yarn</span>
              <span className="plantPhone__pill plantPhone__pill--good">Yes</span>
            </div>
          </div>

          <button
            type="submit"
            className="plantPhone__bigBtn"
            data-testid="plant-submit-check-btn"
            disabled={busy}
            style={{ marginTop: '16px' }}
          >
            Submit (locks on submit)
          </button>
        </form>
      )}

      {activeTab === 'line' && (
        <>
          {/* 1. My line card (h-plt-shift) */}
          <div className="plantPhone__card">
            <div className="plantPhone__row">
              <h3 className="plantPhone__title">
                {linePlace ? linePlace.name : t('NLSOpsPlantPhoneMyLine')}
              </h3>
              <span className="plantPhone__pill plantPhone__pill--good">
                {t('NLSOpsPlantPhoneOfflineSafe')}
              </span>
            </div>
            <div className="plantPhone__meta">
              {linePlace ? `${linePlace.name} · Shift` : 'Line 1 · Shift'}
            </div>
          </div>

          <button
            type="button"
            className="plantPhone__bigBtn"
            data-testid="plant-scan-machine-btn"
            onClick={handleScanClick}
          >
            {t('NLSOpsPlantPhoneScanMachine')}
          </button>

          {/* 2. Machine Card */}
          {selectedAsset && (
            <div className="plantPhone__card" data-testid="plant-machine-card">
              <div className="plantPhone__row">
                <h4 className="plantPhone__title">{selectedAsset.name}</h4>
                <span
                  className={`plantPhone__pill ${
                    selectedAsset.status === 'ISOLATED'
                      ? 'plantPhone__pill--bad'
                      : selectedAsset.status === 'MAINTENANCE'
                        ? 'plantPhone__pill--warn'
                        : 'plantPhone__pill--good'
                  }`}
                >
                  {getMachineStateLabel(selectedAsset)}
                </span>
              </div>

              <div className="plantPhone__meta">
                {selectedAsset.pm?.nextDueKey
                  ? `${t('NLSOpsPlantStatePmDue')} ${selectedAsset.pm.nextDueKey}`
                  : getMachineStateLabel(selectedAsset)}
              </div>

              <div className="plantPhone__row">
                <button
                  type="button"
                  className="plantPhone__btn plantPhone__btn--pri"
                  data-testid="plant-breakdown-btn"
                  onClick={() => setShowBreakdownForm(!showBreakdownForm)}
                >
                  {t('NLSOpsPlantBreakdownBtn')}
                </button>
              </div>
            </div>
          )}

          {/* 3. Breakdown Form (h-plt-breakdown) */}
          {showBreakdownForm && selectedAsset && (
            <form
              className="plantPhone__card"
              data-testid="plant-breakdown-form"
              onSubmit={handleRaiseBreakdown}
            >
              <div className="plantPhone__field">
                <label className="plantPhone__label">{t('NLSOpsPlantMachineLabel')}</label>
                <div className="plantPhone__meta">{selectedAsset.name}</div>
              </div>

              <div className="plantPhone__field">
                <label className="plantPhone__label">{t('NLSOpsPlantReasonLabel')}</label>
                <div className="plantPhone__chips">
                  {PLANT_DOWNTIME_REASONS.map((reason) => {
                    const isActive = selectedReason === reason
                    return (
                      <button
                        key={reason}
                        type="button"
                        className={`plantPhone__chip ${isActive ? 'plantPhone__chip--active' : ''}`}
                        onClick={() => setSelectedReason(reason)}
                      >
                        {reason.charAt(0) + reason.slice(1).toLowerCase().replace(/_/g, ' ')}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="plantPhone__field">
                <label className="plantPhone__label" htmlFor="plant-breakdown-what">
                  {t('NLSOpsPlantWhatLabel')}
                </label>
                <textarea
                  id="plant-breakdown-what"
                  className="plantPhone__textarea"
                  data-testid="plant-breakdown-what-input"
                  value={whatText}
                  onChange={(e) => setWhatText(e.target.value)}
                  placeholder={t('NLSOpsPlantWhatLabel')}
                  rows={3}
                />
              </div>

              <div className="plantPhone__field">
                <button
                  type="button"
                  className="plantPhone__bigBtn plantPhone__bigBtn--sec"
                  data-testid="plant-breakdown-photo-btn"
                >
                  {t('NLSOpsPlantPhonePhotoBtn')}
                </button>
              </div>

              <button
                type="submit"
                className="plantPhone__bigBtn"
                data-testid="plant-raise-breakdown-submit"
                disabled={busy}
              >
                {t('NLSOpsPlantPhoneMarkDownBtn')}
              </button>
            </form>
          )}

          {/* 4. My Checks Card */}
          <div className="plantPhone__card" data-testid="plant-my-checks-card">
            <h4 className="plantPhone__title">{t('NLSOpsPlantHelperMyChecksTitle')}</h4>
            <div className="plantPhone__list">
              {checks.map((chk) => (
                <div
                  key={chk.id}
                  className="plantPhone__row"
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{chk.title}</div>
                    {chk.dueTime && (
                      <div className="plantPhone__meta">{chk.dueTime}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      className={`plantPhone__pill ${
                        chk.status === 'DONE'
                          ? 'plantPhone__pill--good'
                          : chk.status === 'MISSED'
                            ? 'plantPhone__pill--bad'
                            : 'plantPhone__pill--warn'
                      }`}
                    >
                      {chk.status === 'DONE'
                        ? 'Done'
                        : chk.status === 'MISSED'
                          ? 'Missed'
                          : 'Due'}
                    </span>
                    {chk.status !== 'DONE' && (
                      <button
                        type="button"
                        className="plantPhone__btn plantPhone__btn--pri"
                        data-testid={`plant-open-check-btn-${chk.id}`}
                        onClick={() => handleOpenCheck(chk)}
                      >
                        Check
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
